import { IFluidObject } from '@fluidframework/core-interfaces';
import { KeyboardShortcutEmitter, KeyboardShortcut, KeyboardEventInfo } from '@fluidx/office-fluid-types';
import { PostMessageHandlers, PostMessageSender } from '../Shared/PostMessage';
import {
  SetShortcutsMessage,
  KeyboardShortcutForProxy,
  InvokeKeyboardShortcutMessage
} from '../MessageTypes/KeyboardShortcutEmitterMessageTypes';

/**
 * Creates the proxy for the KeyboardShortcutEmitter on an IFluidObject on the
 * guest side.
 * @param sender used for communicating interactions with proxy to guest side
 */
export function getHostSideKeyboardShortcutEmitter(
  sender: PostMessageSender
): {
  component: KeyboardShortcutEmitter;
  handlers: PostMessageHandlers<string>;
  dispose: () => void;
} {
  const generateShortcutId = getShortcutIdGenerator();

  // Keep track of the shortcut handlers so we can invoke them as requested
  let shortcutMap: { [shortcutId: string]: ((keyCombination: KeyboardEventInfo) => void) | undefined } = {};

  const dispose = () => {
    shortcutMap = {};
  };

  const component: KeyboardShortcutEmitter = {
    get KeyboardShortcutEmitter() {
      return this;
    },
    setShortcuts(newShortcuts: KeyboardShortcut[]): void {
      // Reset known shortcuts so it will only contain the new set
      shortcutMap = {};

      const shortcuts = newShortcuts.map((shortcut) => {
        const { handler, keyboardShortcutDescription } = shortcut;
        const shortcutId = generateShortcutId();

        const shortcutForProxy: KeyboardShortcutForProxy = {
          shortcutId,
          keyboardShortcutDescription
        };
        shortcutMap[shortcutId] = handler;

        return shortcutForProxy;
      });

      const message: SetShortcutsMessage = {
        type: 'KeyboardShortcutEmitter_SetShortcuts',
        payload: { shortcuts }
      };
      sender.send(message);
    }
  };

  const handlers: PostMessageHandlers<string> = {
    ['KeyboardShortcutEmitter_InvokeHandler']: (message) => {
      const { shortcutId, eventInfo } = (message as InvokeKeyboardShortcutMessage).payload;
      const shortcutHandler = shortcutMap[shortcutId];
      shortcutHandler && shortcutHandler(eventInfo);
    }
  };

  return { component, handlers, dispose };
}

function getShortcutIdGenerator(): () => string {
  let counter: number = 0;
  return () => {
    /* To keep things simple, increment a counter and use the value for the ID.
     *
     * Since the ID changes if the same keyboard shortcut is given to us twice,
     * there is an edge case which may result in a shortcut not handled
     * correctly: if the keyboard shortcut is invoked between when the host side
     * receives the new set of shortcuts and when the guest side receives the new
     * id, the invoke message will be sent with the old ID which won't match a
     * handler.
     */
    const id = counter.toString();
    counter += 1;
    return id;
  };
}

/**
 * Creates the message handlers needed to communicate with a proxy of the
 * KeyboardShortcutEmitter on the host side.
 * @param component the component being driven via proxy
 * @param sender used for communicating interactions with proxy to host side
 */
export function getGuestSideKeyboardShortcutEmitter(
  component: IFluidObject,
  sender: PostMessageSender
): PostMessageHandlers<string> | undefined {
  const keyboardShortcutEmitter = component.KeyboardShortcutEmitter;
  if (!keyboardShortcutEmitter) {
    return undefined;
  }

  return {
    ['KeyboardShortcutEmitter_SetShortcuts']: (message) => {
      const { shortcuts } = (message as SetShortcutsMessage).payload;

      const shortcutProxies = shortcuts.map((shortcut) => {
        const { shortcutId, ...shortcutInfo } = shortcut;
        const handler = (eventInfo: KeyboardEventInfo) => {
          const message: InvokeKeyboardShortcutMessage = {
            type: 'KeyboardShortcutEmitter_InvokeHandler',
            payload: {
              shortcutId,
              eventInfo
            }
          };
          sender.send(message);
        };

        const shortcutProxy: KeyboardShortcut = { ...shortcutInfo, handler };
        return shortcutProxy;
      });

      keyboardShortcutEmitter.setShortcuts(shortcutProxies);
    }
  };
}
